import { cn } from '@/lib/cn';
import { statusCopy, type ChipTone } from '@/features/chat/copy/statuses';
import type { ChatStatus, ClientChatStatus } from '@/api/models';

/**
 * The status chip.
 *
 * A wash, a lamp and a word. The lamp carries the state, the word repeats it —
 * so the chip never depends on colour alone — and the wash is the only place on
 * a client screen where a state colour touches a surface.
 *
 * `attention` and `active` are separated here in a way Graphite could not
 * manage: work in progress wears mint and an ink lamp, a question waiting on
 * the reader wears blush and the accent. Both pulse, because both mean the run
 * is alive; the colour says whose move it is.
 *
 * `resting` has no lamp at all. It is the only state that is not an event, and
 * a tick reads as "settled" where a dot reads as "something is happening".
 */

const TONE_STYLES: Record<ChipTone, string> = {
  resting: 'bg-bone text-graphite-900',
  active: 'bg-mint text-graphite-900',
  attention: 'bg-blush text-signal',
  fault: 'bg-alarm-wash text-alarm',
  held: 'bg-bone text-graphite-500',
};

const LAMP_STYLES: Record<ChipTone, string> = {
  resting: 'bg-graphite-300',
  active: 'bg-graphite-900 animate-ledger-pulse',
  attention: 'bg-signal animate-ledger-pulse',
  fault: 'bg-alarm',
  held: 'bg-transparent shadow-[inset_0_0_0_1.5px_var(--ink-3)]',
};

function Lamp({ tone }: { tone: ChipTone }) {
  // Settled is a tick, not a lamp: nothing is running, so nothing should blink.
  if (tone === 'resting') {
    return (
      <svg aria-hidden viewBox="0 0 12 12" className="h-3 w-3 shrink-0 stroke-current" fill="none" strokeWidth="1.6">
        <path d="M2 6.4 4.6 9 10 3.2" />
      </svg>
    );
  }
  // The fault lamp keeps a glyph rather than a dot: a failure is the one state
  // worth recognising from shape at a glance, before the word is read.
  if (tone === 'fault') {
    return (
      <svg aria-hidden viewBox="0 0 12 12" className="h-3 w-3 shrink-0 stroke-current" fill="none" strokeWidth="1.6">
        <circle cx="6" cy="6" r="4.6" />
        <path d="M4.2 7.8 7.8 4.2" />
      </svg>
    );
  }
  return <span aria-hidden className={cn('h-[7px] w-[7px] shrink-0 rounded-full', LAMP_STYLES[tone])} />;
}

export function StatusChip({
  status,
  className,
  size = 'md',
  awaitingAnswer = false,
}: {
  status: ClientChatStatus | ChatStatus | string | undefined;
  className?: string;
  size?: 'sm' | 'md';
  /**
   * A parked question, from `ClientProjectSummary.awaiting_answer`.
   *
   * It used to render as a second badge beside this chip. It is the chip now:
   * the status underneath is `agent running` either way, and "Working on it"
   * next to "Needs your answer" made the reader work out which of the two was
   * addressed to them. Whose move it is outranks what the run is doing.
   */
  awaitingAnswer?: boolean;
}) {
  const base = statusCopy(status);
  const copy = awaitingAnswer
    ? { ...base, label: 'Needs your answer', tone: 'attention' as const, detail: base.detail }
    : base;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-2 whitespace-nowrap rounded-full font-semibold tracking-[-0.005em]',
        size === 'sm' ? 'h-7 px-3 text-[0.75rem]' : 'h-[30px] px-3.5 text-[0.78125rem]',
        TONE_STYLES[copy.tone],
        className,
      )}
      title={copy.detail}
    >
      <Lamp tone={copy.tone} />
      {copy.label}
    </span>
  );
}
